import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import { Popover, Tooltip, Input } from 'antd';
import {
  Plus,
  X,
  Search,
  Paperclip,
  BriefcaseBusiness,
  Users,
  Building2,
  ListTodo,
  StickyNote,
  UserRound,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactElement } from 'react';

import { listDeals } from '../../deals/api/deals.client.js';
import { listContacts } from '../../contacts/api/contacts.client.js';
import { listCompanies } from '../../companies/api/companies.client.js';
import { listTasks } from '../../tasks/api/tasks.client.js';
import { listNotes } from '../../notes/api/notes.client.js';
import { listTeamMembers } from '../../team/api/team.client.js';

export type ChatEntityChipType =
  | 'deal'
  | 'contact'
  | 'company'
  | 'task'
  | 'note'
  | 'user'
  | 'attachment';

/**
 * Chip-ссылка на сущность. Каждый вариант — подмножество `ChatContextRef` из
 * `@govard/ai-chat-kit`: массив таких чипов передаётся в `agent.send` как
 * `contextRefs` без преобразований.
 */
export type ChatEntityChip =
  | { type: 'deal'; id: string; label: string }
  | { type: 'contact'; id: string; label: string }
  | { type: 'company'; id: string; label: string }
  | { type: 'task'; id: string; label: string }
  | { type: 'note'; id: string; label: string }
  | { type: 'user'; id: string; label: string }
  | { type: 'attachment'; id: string; label: string };

export interface EntityChipPickerProps {
  chips: ChatEntityChip[];
  onAdd: (chip: ChatEntityChip) => void;
  onRemove: (chip: ChatEntityChip) => void;
  disabled?: boolean;
  /** Maximum number of chips allowed; the picker blocks additions beyond it. */
  max?: number;
  /** Display mode: 'full' (both chips and button), 'chips-only', or 'button-only'. Defaults to 'full'. */
  mode?: 'full' | 'chips-only' | 'button-only';
}

/** Вариант @-меню: тип сущности + опция из общего поиска по всем типам. */
export interface MentionOption {
  type: ChatEntityChipType;
  id: string;
  label: string;
}

interface EntityOption {
  id: string;
  label: string;
}

const OPTIONS_LIMIT = 5;
export const DEFAULT_ENTITY_CHIP_MAX = 8;

const ENTITY_TYPE_ICONS: Record<ChatEntityChipType, typeof Plus> = {
  deal: BriefcaseBusiness,
  contact: Users,
  company: Building2,
  task: ListTodo,
  note: StickyNote,
  user: UserRound,
  attachment: Paperclip,
};

function typeLabelKey(type: ChatEntityChipType): string {
  return `crm.ai_chat.context_chips.${type}`;
}

/** Строит дискриминированный чип из типа и опции. */
function buildChip(type: ChatEntityChipType, id: string, label: string): ChatEntityChip {
  switch (type) {
    case 'deal':
      return { type, id, label };
    case 'contact':
      return { type, id, label };
    case 'company':
      return { type, id, label };
    case 'task':
      return { type, id, label };
    case 'note':
      return { type, id, label };
    case 'user':
      return { type, id, label };
    case 'attachment':
      return { type, id, label };
  }
}

async function fetchOptions(type: ChatEntityChipType, query: string): Promise<EntityOption[]> {
  const q = query.trim();
  switch (type) {
    case 'deal': {
      const deals = await listDeals(q.length > 0 ? { q } : {});
      return deals.slice(0, OPTIONS_LIMIT).map((deal) => ({ id: deal.id, label: deal.title }));
    }
    case 'contact': {
      const contacts = await listContacts(q.length > 0 ? { q } : {});
      return contacts
        .slice(0, OPTIONS_LIMIT)
        .map((contact) => ({ id: contact.id, label: contact.fullName }));
    }
    case 'company': {
      const companies = await listCompanies();
      const filtered =
        q.length > 0
          ? companies.filter((company) => company.name.toLowerCase().includes(q.toLowerCase()))
          : companies;
      return filtered
        .slice(0, OPTIONS_LIMIT)
        .map((company) => ({ id: company.id, label: company.name }));
    }
    case 'task': {
      const tasks = await listTasks(q.length > 0 ? { q } : {});
      return tasks.slice(0, OPTIONS_LIMIT).map((task) => ({ id: task.id, label: task.title }));
    }
    case 'note': {
      const notes = await listNotes(q.length > 0 ? { q } : {});
      return notes.slice(0, OPTIONS_LIMIT).map((note) => ({
        id: note.id,
        label: note.body.length > 60 ? `${note.body.slice(0, 60)}…` : note.body,
      }));
    }
    case 'user': {
      const members = await listTeamMembers();
      const filtered =
        q.length > 0
          ? members.filter(
              (member) =>
                member.name.toLowerCase().includes(q.toLowerCase()) ||
                member.email.toLowerCase().includes(q.toLowerCase()),
            )
          : members;
      return filtered
        .slice(0, OPTIONS_LIMIT)
        .map((member) => ({ id: member.id, label: member.name || member.email }));
    }
    case 'attachment':
      return [];
  }
}

/**
 * Общий поиск по всем шести типам сущностей для @-меню. Каждый тип вносит до
 * OPTIONS_LIMIT вариантов; порядок — тип за типом (сделки, контакты, компании,
 * задачи, заметки, пользователи).
 */
export async function searchEntityOptions(query: string): Promise<MentionOption[]> {
  const types: ChatEntityChipType[] = ['deal', 'contact', 'company', 'task', 'note', 'user'];
  const byType = await Promise.allSettled(
    types.map(async (type) => {
      const options = await fetchOptions(type, query);
      return options.map((option) => ({ type, id: option.id, label: option.label }));
    }),
  );
  return byType.flatMap((result) => (result.status === 'fulfilled' ? result.value : []));
}

export function EntityChipPicker({
  chips,
  onAdd,
  onRemove,
  disabled = false,
  max = DEFAULT_ENTITY_CHIP_MAX,
  mode = 'full',
}: EntityChipPickerProps): ReactElement | null {
  useI18nSubscription();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeType, setActiveType] = useState<ChatEntityChipType | null>(null);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<EntityOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const atMax = chips.length >= max;

  const resetPicker = (): void => {
    setPickerOpen(false);
    setActiveType(null);
    setQuery('');
    setOptions([]);
    setIsLoading(false);
  };

  useEffect(() => {
    if (!pickerOpen) {
      return;
    }
    setActiveType(null);
    setQuery('');
    setOptions([]);
  }, [pickerOpen]);

  useEffect(() => {
    if (!pickerOpen || activeType === null) {
      setOptions([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    void fetchOptions(activeType, query)
      .then((items) => {
        if (!cancelled) setOptions(items);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pickerOpen, activeType, query]);

  const pickerContent = useMemo(() => {
    const typeOptions: Array<{ type: ChatEntityChipType; label: string }> = [
      { type: 'deal', label: i18n.t(typeLabelKey('deal')) },
      { type: 'contact', label: i18n.t(typeLabelKey('contact')) },
      { type: 'company', label: i18n.t(typeLabelKey('company')) },
      { type: 'task', label: i18n.t(typeLabelKey('task')) },
      { type: 'note', label: i18n.t(typeLabelKey('note')) },
      { type: 'user', label: i18n.t(typeLabelKey('user')) },
    ];

    if (activeType === null) {
      return (
        <div className="w-56 space-y-1">
          <p className="px-1 text-[10px] font-bold uppercase tracking-wider opacity-80">
            {i18n.t('crm.ai_chat.context_chips.attach')}
          </p>
          {typeOptions.map((option) => {
            const Icon = ENTITY_TYPE_ICONS[option.type];
            return (
              <button
                key={option.type}
                type="button"
                data-testid={`chat-entity-type-${option.type}`}
                onClick={() => setActiveType(option.type)}
                className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-inherit transition hover:bg-[var(--ant-control-item-bg-hover)]"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[var(--ant-color-fill-quaternary)] opacity-80">
                  <Icon size={13} />
                </span>
                {option.label}
              </button>
            );
          })}
        </div>
      );
    }

    return (
      <div className="w-64 space-y-1.5">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => {
              setActiveType(null);
              setQuery('');
              setOptions([]);
            }}
            className="shrink-0 cursor-pointer rounded-md px-1.5 py-1 text-xs text-inherit opacity-80 transition hover:bg-[var(--ant-control-item-bg-hover)]"
            aria-label={i18n.t('crm.ai_chat.context_chips.back')}
          >
            ←
          </button>
          <Input
            size="small"
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={i18n.t('crm.ai_chat.context_chips.search')}
            prefix={<Search size={12} className="opacity-80" />}
            data-testid="chat-entity-search"
          />
        </div>
        <div className="max-h-52 overflow-y-auto space-y-0.5 crm-microscroll">
          {isLoading ? (
            <p className="px-1 py-2 text-[11px] opacity-80">{i18n.t('crm.ai_chat.typing')}</p>
          ) : options.length === 0 ? (
            <p className="px-1 py-2 text-[11px] opacity-80">
              {i18n.t('crm.ai_chat.context_chips.empty')}
            </p>
          ) : (
            options.map((option) => (
              <button
                key={option.id}
                type="button"
                data-testid={`chat-entity-option-${option.id}`}
                onClick={() => {
                  onAdd(buildChip(activeType, option.id, option.label));
                  resetPicker();
                }}
                className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-inherit transition hover:bg-[var(--ant-control-item-bg-hover)]"
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      </div>
    );
  }, [activeType, isLoading, onAdd, options, query]);

  const buttonElement = (
    <Popover
      open={pickerOpen}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          setPickerOpen(true);
        } else {
          resetPicker();
        }
      }}
      placement="topLeft"
      trigger="click"
      content={pickerContent}
      overlayClassName="crm-popover-nopad"
    >
      <Tooltip
        title={
          atMax
            ? i18n.t('crm.ai_chat.context_chips.max_reached')
            : i18n.t('crm.ai_chat.context_chips.attach')
        }
      >
        <button
          type="button"
          data-testid="chat-entity-picker-add"
          aria-label={i18n.t('crm.ai_chat.context_chips.attach')}
          disabled={disabled}
          className={`inline-flex items-center gap-1.5 rounded-lg border px-2 py-0.5 text-xs font-medium transition cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 ${
            pickerOpen
              ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300'
              : 'border-stone-200/90 dark:border-stone-700/90 bg-stone-50/80 dark:bg-stone-800/80 text-stone-600 dark:text-stone-300 hover:border-amber-300 hover:text-amber-700 hover:bg-amber-50/50'
          }`}
        >
          <Plus size={13} />
          {chips.length === 0 && (
            <span className="text-[11px] font-normal">
              {i18n.t('crm.ai_chat.context_chips.attach')}
            </span>
          )}
        </button>
      </Tooltip>
    </Popover>
  );

  if (mode === 'button-only') {
    return buttonElement;
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 pb-1.5" data-testid="chat-entity-chips">
      {chips.map((chip) => (
        <span
          key={`${chip.type}:${chip.id}`}
          data-testid="chat-entity-chip"
          className="inline-flex items-center gap-1 rounded-md border border-amber-200/80 bg-amber-50/80 px-2 py-0.5 text-[11px] text-amber-950"
        >
          {chip.type === 'attachment' ? (
            <Paperclip size={11} className="text-amber-800 shrink-0" />
          ) : (
            <span className="font-medium text-amber-800">{i18n.t(typeLabelKey(chip.type))}:</span>
          )}
          <span className="max-w-40 truncate">{chip.label}</span>
          <button
            type="button"
            data-testid="chat-entity-chip-remove"
            aria-label={i18n.t('crm.ai_chat.context_chips.remove')}
            onClick={() => onRemove(chip)}
            className="cursor-pointer rounded p-0.5 text-stone-400 transition hover:bg-amber-100 hover:text-stone-700"
          >
            <X size={11} />
          </button>
        </span>
      ))}
      {buttonElement}
    </div>
  );
}
